import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Grain, HubResponse, HubScope, PeriodMetrics } from "@/lib/consultant-hub";
import { cn } from "@/lib/utils";

export const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US");
export const fmtMoney = (n: number | null) =>
  n === null ? "—" : `$${Math.round(n).toLocaleString("en-US")}`;
export const fmtMoneyShort = (n: number) =>
  Math.abs(n) >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(2)}M`
    : Math.abs(n) >= 10_000
      ? `$${Math.round(n / 1000)}k`
      : fmtMoney(n);
export const fmtPct = (n: number | null) => (n === null ? "—" : `${(n * 100).toFixed(1)}%`);
export const fmtK = (k: number | null) => (k === null ? "—" : k.toFixed(2));

/** Year-over-year change as a signed % string, or null when there's no base. */
function yoy(cur: number, prior: number): { text: string; up: boolean } | null {
  if (!prior) return null;
  const d = (cur - prior) / prior;
  return { text: `${d >= 0 ? "+" : ""}${(d * 100).toFixed(0)}%`, up: d >= 0 };
}

export function YoyBadge({ cur, prior }: { cur: number; prior: number }) {
  const v = yoy(cur, prior);
  if (!v) return null;
  return (
    <span className={cn("text-xs font-medium", v.up ? "text-emerald-600" : "text-rose-600")}>
      {v.text} vs LY
    </span>
  );
}

/* ---- Filters that carry over between Consultant Hub pages ----
 * Grain, fiscal year and product are remembered in this browser so moving from
 * the summary to a division or territory keeps the same view. Storage can be
 * unavailable (private window, blocked site data) — then they just reset.
 * Pages open on engagement rings (Austin, 2026-10-22); the key was bumped to v2
 * so browsers that had saved "All products" pick up the new default once. */
const FILTER_STORAGE_KEY = "consultant-hub-filters-v2";
interface HubFilters {
  grain: Grain;
  fy: number | null;
  product: string;
}
const DEFAULT_FILTERS: HubFilters = { grain: "month", fy: null, product: "engagement" };

function readFilters(): HubFilters {
  try {
    const raw = JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) ?? "{}") as Partial<HubFilters>;
    return {
      grain: raw.grain === "quarter" || raw.grain === "year" ? raw.grain : "month",
      fy: typeof raw.fy === "number" ? raw.fy : null,
      product: typeof raw.product === "string" ? raw.product : DEFAULT_FILTERS.product,
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

export function useHubFilters() {
  const [filters, setFilters] = useState<HubFilters>(readFilters);
  const update = (patch: Partial<HubFilters>) =>
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // storage unavailable — the choice still applies on this page
      }
      return next;
    });
  return {
    ...filters,
    setGrain: (grain: Grain) => update({ grain }),
    setFy: (fy: number) => update({ fy }),
    setProduct: (product: string) => update({ product }),
  };
}

export function useHubData(grain: Grain, fy: number | null, product: string) {
  const [data, setData] = useState<HubResponse | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    setError("");
    const qs = new URLSearchParams({ grain, product });
    if (fy) qs.set("fy", String(fy));
    fetch(`/api/consultant-hub?${qs}`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok)
          throw new Error(
            r.status === 403
              ? "You don't have access to the Consultant Hub."
              : "Couldn't load the Consultant Hub.",
          );
        return (await r.json()) as HubResponse;
      })
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [grain, fy, product]);
  return { data, error, loading };
}

export function Toggle<T extends string | number>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-md border bg-background p-0.5">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded px-3 py-1 text-sm font-medium transition-colors",
            value === o.value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Product switch shown on both hub pages; every number on the page follows it. */
export function ProductSelect({
  data,
  value,
  onChange,
}: {
  data: HubResponse | null;
  value: string;
  onChange: (v: string) => void;
}) {
  const options = data?.productOptions ?? [{ key: "all", label: "All products" }];
  return (
    <select
      className="h-8 rounded-md border bg-background px-2 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.key} value={o.key}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export const GRAIN_OPTIONS: { value: Grain; label: string }[] = [
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
  { value: "year", label: "Annual" },
];

export function FySelect({
  data,
  fy,
  onChange,
}: {
  data: HubResponse | null;
  fy: number | null;
  onChange: (fy: number) => void;
}) {
  if (!data) return null;
  return (
    <select
      className="h-8 rounded-md border bg-background px-2 text-sm"
      value={fy ?? data.fy}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      {data.fyOptions.map((o) => (
        <option key={o.fy} value={o.fy}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export interface ScoreboardRow {
  scope: HubScope;
  /** Division rows are bold and tinted; territory rows are indented under them
   * with their consultant; "other" rows (no state on file) stand on their own. */
  level: "division" | "territory" | "other";
}

const sbTh = "px-3 py-2 text-right font-medium text-muted-foreground whitespace-nowrap";
const sbTd = "px-3 py-2 text-right tabular-nums whitespace-nowrap";

function ScoreCells({ m }: { m: PeriodMetrics }) {
  return (
    <>
      <td className={cn(sbTd, "font-semibold")}>{fmtK(m.k)}</td>
      <td className={sbTd}>{fmtK(m.k12)}</td>
      <td className={sbTd}>{fmtInt(m.opps)}</td>
      <td className={sbTd}>{fmtInt(m.closed)}</td>
      <td className={sbTd}>{fmtPct(m.closeRate)}</td>
      <td className={sbTd}>{fmtMoney(m.gross)}</td>
      <td className={sbTd}>{fmtMoney(m.net)}</td>
      <td className={sbTd}>{fmtMoney(m.avgSale)}</td>
    </>
  );
}

/** One row per division / territory with the headline numbers, plus a total row. */
export function ScopeScoreboard({
  rows,
  metricsFor,
  total,
  totalLabel,
}: {
  rows: ScoreboardRow[];
  metricsFor: (scopeKey: string) => PeriodMetrics | null;
  total: PeriodMetrics | null;
  totalLabel: string;
}) {
  return (
    <table className="w-full text-sm">
      <thead className="border-b bg-muted/40">
        <tr>
          <th className="px-3 py-2 text-left font-medium text-muted-foreground">Territory</th>
          <th className={sbTh}>Fiscal YTD K</th>
          <th className={sbTh}>Rolling K</th>
          <th className={sbTh}>Opps</th>
          <th className={sbTh}>Closed</th>
          <th className={sbTh}>Close rate</th>
          <th className={sbTh}>Gross</th>
          <th className={sbTh}>Net</th>
          <th className={sbTh}>Avg sale</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ scope: s, level }) => {
          const m = metricsFor(s.key);
          if (!m) return null;
          return (
            <tr
              key={s.key}
              className={cn(
                "border-b last:border-0",
                level === "division" && "bg-primary/5 font-semibold",
              )}
            >
              <td className={cn("px-3 py-2", level === "territory" && "pl-7")}>
                <div className={cn(level === "division" ? "font-semibold" : "font-medium")}>
                  {s.label}
                </div>
                {level === "division" && s.divisionHead && (
                  <div className="text-xs font-normal text-muted-foreground">
                    Led by {s.divisionHead}
                  </div>
                )}
                {level === "territory" && s.consultant && (
                  <div className="text-xs text-muted-foreground">{s.consultant}</div>
                )}
              </td>
              <ScoreCells m={m} />
            </tr>
          );
        })}
        {total && (
          <tr className="border-t-2 bg-muted/30 font-semibold">
            <td className="px-3 py-2">{totalLabel}</td>
            <ScoreCells m={total} />
          </tr>
        )}
      </tbody>
    </table>
  );
}

/** One box in the K equation. */
function KTerm({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div
      className={cn(
        "min-w-28 rounded-lg border px-4 py-3 text-center",
        strong ? "border-primary bg-primary/5" : "bg-muted/30",
      )}
    >
      <div className={cn("font-serif tabular-nums", strong ? "text-3xl text-primary" : "text-2xl")}>
        {value}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

const Op = ({ children }: { children: string }) => (
  <span className="text-2xl font-light text-muted-foreground">{children}</span>
);

/**
 * The Fiscal YTD K equation laid out with this year's real numbers:
 * (Friends & family + Affiliate) ÷ last year's closes over the same dates = K.
 */
export function KBreakdown({
  m,
  asOfLabel,
  note,
  className,
}: {
  m: PeriodMetrics;
  asOfLabel: string;
  note?: string;
  className?: string;
}) {
  const p = m.kParts;
  if (!p) return null;
  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          How K is calculated{" "}
          <span className="font-normal text-muted-foreground">({asOfLabel})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <KTerm label="Friends & family closes" value={fmtInt(p.friend)} />
          <Op>+</Op>
          <KTerm label="Affiliate closes" value={fmtInt(p.affiliate)} />
          <Op>=</Op>
          <KTerm label="Total relational closes" value={fmtInt(p.relational)} />
          <Op>÷</Op>
          <KTerm label="Total closed deals last year, same dates" value={fmtInt(p.lyClosed)} />
          <Op>=</Op>
          <KTerm label="Fiscal YTD K" value={fmtK(m.k)} strong />
        </div>
        <p className="text-xs text-muted-foreground">
          Relational here means Friend + Affiliate only (Past Client and other sources don't count
          toward K). This year's closes run from April 1 to date; last year's closes cover the same
          dates one year earlier. {note}
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * The K equation for every division and territory side by side:
 * last year's closes, Friends & family, Affiliate, relational total, and K.
 */
export function KByTerritory({
  rows,
  metricsFor,
  total,
  totalLabel,
  asOfLabel,
  className,
}: {
  rows: ScoreboardRow[];
  metricsFor: (scopeKey: string) => PeriodMetrics | null;
  total: PeriodMetrics | null;
  totalLabel: string;
  asOfLabel: string;
  className?: string;
}) {
  const cells = (m: PeriodMetrics) => {
    const p = m.kParts;
    return (
      <>
        <td className={sbTd}>{p ? fmtInt(p.friend) : "—"}</td>
        <td className={sbTd}>{p ? fmtInt(p.affiliate) : "—"}</td>
        <td className={cn(sbTd, "font-medium")}>{p ? fmtInt(p.relational) : "—"}</td>
        <td className={sbTd}>{p ? fmtInt(p.lyClosed) : "—"}</td>
        <td className={cn(sbTd, "border-l font-semibold")}>{fmtK(m.k)}</td>
      </>
    );
  };
  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          K by territory <span className="font-normal text-muted-foreground">({asOfLabel})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Territory</th>
              <th className={sbTh}>Friends &amp; family</th>
              <th className={sbTh}>+ Affiliate</th>
              <th className={sbTh}>= Relational</th>
              <th className={sbTh}>÷ Last year's closes</th>
              <th className={cn(sbTh, "border-l text-foreground")}>= Fiscal YTD K</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ scope: s, level }) => {
              const m = metricsFor(s.key);
              if (!m) return null;
              return (
                <tr
                  key={s.key}
                  className={cn(
                    "border-b last:border-0",
                    level === "division" && "bg-primary/5 font-semibold",
                  )}
                >
                  <td className={cn("px-3 py-2", level === "territory" && "pl-7")}>
                    <div className={cn(level === "division" ? "font-semibold" : "font-medium")}>
                      {s.label}
                    </div>
                    {level === "territory" && s.consultant && (
                      <div className="text-xs font-normal text-muted-foreground">
                        {s.consultant}
                      </div>
                    )}
                  </td>
                  {cells(m)}
                </tr>
              );
            })}
            {total && (
              <tr className="border-t-2 bg-muted/30 font-semibold">
                <td className="px-3 py-2">{totalLabel}</td>
                {cells(total)}
              </tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
